// AccountFlows.tsx · v12.10.11.1 (#1462 common-password rule wording B - no "leaked") · 2026-10-09
// AccountFlows.tsx · v12.10.9.4 (#1342 F9 password rules; #1336 no sample text in sign-in boxes) · 2026-10-07 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// AccountFlows.tsx - the self-service flows opened from Your account (#1256 / #1259):
//   ChangePasswordModal  - current + new + confirm, live rules (Login spec L7) -> POST /api/auth/change-password
//   AuthenticatorSetup   - QR of otpauthUri + key in groups of 4 + 6-digit check (Login prototype L5a) -> backup codes
//   TextCodeSetup        - mobile -> texted code -> backup codes
//   BackupCodesModal     - the 10 codes, shown once: Download .txt / Copy / "I've saved these codes" (L5b)
// Every call that the server may guard with Confirm-it's-you goes through withConfirm().
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { accountAuthApi, errorBody } from '../../lib/securityApi';
import { authApi } from '../../lib/authApi';
import { COMMON_PASSWORD_LINE, COMMON_RULE, MIX_TICK, isCommonPassword, mixOk, normMix, type CharMix } from '@shared-ui/commonPasswords';   // v12.10.9.4 (#1342); v12.10.11.1 (#1462) COMMON_RULE
import { notifySecurityChanged } from '../../lib/useAccess';
import { toast } from '../../lib/toast';
import { isConfirmCancelled, withConfirm } from './ConfirmItsYou';
import { QrCode } from './QrCode';
import { Ic, InlineError, ScModal, digits, plural } from './ui';

function codeErr(err: unknown): string {
  const b = errorBody(err);
  if (err instanceof ApiError) {
    if (err.status === 429 || b.error === 'too_many_codes') return `Too many codes. Try again in ${plural(Math.max(1, Math.ceil((b.retryInSeconds ?? 60) / 60)), 'minute')}.`;
    if (b.error === 'bad_code' || err.status === 401) return `That code isn’t right${typeof b.triesLeft === 'number' ? ` - ${plural(b.triesLeft, 'try', 'tries')} left` : ''}. Check the time on your phone and try the next code.`;
  }
  return b.message ?? 'That didn’t work. Try again.';
}

// ── Change password ───────────────────────────────────────────────────────────
export function ChangePasswordModal({ minLength, complexity, charMix, nameParts, onClose }: {
  minLength: number; complexity: boolean; charMix?: CharMix; nameParts: string[]; onClose: () => void;
}) {
  // v12.10.9.4 (#1342 F9): the practice's own words (for the common-password check) come with the password rules.
  const [context, setContext] = useState<(string | null)[]>([]);
  useEffect(() => { authApi.passwordRules(window.location.hostname.split('.')[0] || null).then((r) => setContext(r.context ?? [])).catch(() => undefined); }, []);
  const [n1Settled, setN1Settled] = useState('');
  const [cur, setCur] = useState('');
  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const curRef = useRef<HTMLInputElement>(null);
  useEffect(() => { curRef.current?.focus({ preventScroll: true }); }, []);
  useEffect(() => { const t = window.setTimeout(() => setN1Settled(n1), 350); return () => window.clearTimeout(t); }, [n1]);

  const low = n1.toLowerCase();
  const parts = nameParts.map((s) => s.trim().toLowerCase()).filter((s) => s.length >= 3);
  const rules: [string, boolean][] = [
    [`At least ${minLength} characters`, n1.length >= minLength],
    ['Not the same as your email or name', n1.length > 0 && !parts.some((p) => low.includes(p))],
    ['Different from your current password', n1.length > 0 && n1 !== cur],
  ];
  const common = n1Settled.length > 0 && isCommonPassword(n1Settled, [...context, ...nameParts]);
  rules.splice(2, 0, [COMMON_RULE, n1.length > 0 && !common]);
  const mix = normMix(charMix, complexity);
  if (mix !== 'any') rules.push([MIX_TICK[mix], mixOk(n1, mix)]);
  const score = rules.filter((r) => r[1]).length;
  const pct = n1 ? Math.round((score / rules.length) * 100) : 0;
  const meterColor = score === rules.length ? '#16a34a' : score >= Math.ceil(rules.length / 2) ? '#d97706' : '#dc2626';
  const mismatch = n2.length > 0 && n2 !== n1;
  const ok = cur.length > 0 && score === rules.length && n2 === n1;

  async function save() {
    if (!ok || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await accountAuthApi.changePassword(cur, n1);
      const others = r?.signedOutOthers ?? 0;
      toast('success', 'Password changed', others ? `You were signed out on ${plural(others, 'other device')}.` : undefined);
      notifySecurityChanged();
      onClose();
    } catch (e) {
      const b = errorBody(e);
      if (b.error === 'rules' && b.failed?.length) setErr(`That password doesn’t meet the rules: ${b.failed.join('; ')}.`);
      else if (e instanceof ApiError && (e.status === 401 || b.error === 'invalid')) setErr('Your current password isn’t right.');
      else setErr(b.message ?? 'Your password wasn’t changed. Try again.');
    } finally { setBusy(false); }
  }

  return (
    <ScModal onClose={onClose} label="Change password" closeOnScrim={false}>
      <span className="sev info"><Ic k="key" /></span>
      <h4>Change password</h4>
      <p>Your other devices are signed out when you save.</p>
      <div className="mrow"><label htmlFor="cpCur">Current password</label>
        <input id="cpCur" ref={curRef} type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
      <div className="mrow"><label htmlFor="cpN1">New password</label>
        <input id="cpN1" type="password" autoComplete="new-password" value={n1} onChange={(e) => setN1(e.target.value)} />
        <div className="sc-meter"><i style={{ width: `${pct}%`, background: meterColor }} /></div>
        <div className="sc-pwrules">{rules.map(([t, good]) => <div key={t} className={n1 ? (good ? 'ok' : 'no') : ''}>{t}</div>)}</div>
        {common && <div className="sc-pwbad" role="alert">{COMMON_PASSWORD_LINE}</div>}
      </div>
      <div className="mrow"><label htmlFor="cpN2">Confirm new password</label>
        <input id="cpN2" type="password" autoComplete="new-password" value={n2} onChange={(e) => setN2(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
        {mismatch && <span className="sc-err">Passwords don’t match.</span>}
      </div>
      {err && <div style={{ marginBottom: 12 }}><InlineError text={err} /></div>}
      <div className="mact">
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        <button type="button" className="ma" disabled={!ok || busy} onClick={() => void save()}><Ic k="check" /> {busy ? 'Saving…' : 'Save password'}</button>
      </div>
    </ScModal>
  );
}

// ── Backup codes (shown once) ───────────────────────────────────────────────────
function downloadCodes(codes: string[], who: string) {
  const text = [
    'VoiceSpectrum - backup codes',
    who ? `For: ${who}` : '',
    `Created: ${new Date().toLocaleString('en-US')}`,
    'Each code works once. Keep them somewhere safe.',
    '',
    ...codes,
    '',
  ].filter((l, i) => i !== 1 || l).join('\r\n');
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'voicespectrum-backup-codes.txt';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function BackupCodesBody({ codes, who, onDone }: { codes: string[]; who: string; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [dl, setDl] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <>
      <span className="sev info"><Ic k="codes" /></span>
      <h4>Save your backup codes</h4>
      <p>If you lose your phone, each code gets you in once. Keep them somewhere safe - they won’t be shown again.</p>
      <div className="sc-codes" aria-label="Backup codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="sc-btnrow">
        <button type="button" className="sc-secondary" style={{ flex: '1 1 140px', justifyContent: 'center' }} onClick={() => { downloadCodes(codes, who); setDl(true); }}>
          <Ic k="download" /> {dl ? 'Downloaded' : 'Download .txt'}
        </button>
        <button type="button" className="sc-secondary" style={{ flex: '1 1 140px', justifyContent: 'center' }}
          onClick={() => { void navigator.clipboard?.writeText(codes.join('\n')).then(() => setCopied(true)).catch(() => setCopied(false)); }}>
          <Ic k="copy" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <label className="sc-chk"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I’ve saved these codes</label>
      <div className="mact" style={{ marginTop: 16 }}>
        <button type="button" className="ma" disabled={!saved} onClick={onDone}><Ic k="check" /> Done</button>
      </div>
    </>
  );
}

export function BackupCodesModal({ codes, who, onClose }: { codes: string[]; who: string; onClose: () => void }) {
  // Closing without ticking is allowed (Esc / ×) - the codes are already active; the tick only gates Done.
  return (
    <ScModal onClose={onClose} label="Save your backup codes" closeOnScrim={false}>
      <BackupCodesBody codes={codes} who={who} onDone={onClose} />
    </ScModal>
  );
}

function groupKey(secret: string): string {
  return secret.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
}

// ── Authenticator app (L5a) ─────────────────────────────────────────────────────
export function AuthenticatorSetup({ replacing, who, onClose }: { replacing: boolean; who: string; onClose: () => void }) {
  const [start, setStart] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    withConfirm(() => accountAuthApi.totpStart(), replacing ? 'You’re replacing your authenticator app.' : 'You’re adding an authenticator app to your account.')
      .then((r) => { if (alive) setStart(r); })
      .catch((e) => {
        if (!alive) return;
        if (isConfirmCancelled(e)) { onClose(); return; }
        setLoadErr(errorBody(e).message ?? 'The set-up code couldn’t be created. Close this and try again.');
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (start) codeRef.current?.focus({ preventScroll: true }); }, [start]);

  async function verify() {
    if (code.length !== 6 || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await withConfirm(() => accountAuthApi.totpConfirm(code));
      notifySecurityChanged();
      if (r?.backupCodes?.length) setCodes(r.backupCodes);
      else { toast('success', 'Authenticator app set up'); onClose(); }
    } catch (e) {
      if (isConfirmCancelled(e)) return;
      setErr(codeErr(e)); setCode('');
    } finally { setBusy(false); }
  }

  if (codes) return <BackupCodesModal codes={codes} who={who} onClose={() => { toast('success', 'Authenticator app set up'); onClose(); }} />;

  return (
    <ScModal onClose={onClose} label="Set up your authenticator app" closeOnScrim={false}>
      <span className="sev info"><Ic k="app" /></span>
      <h4>{replacing ? 'Replace your authenticator app' : 'Set up your authenticator app'}</h4>
      <p className="sc-steps">
        1 · Open Google Authenticator, Microsoft Authenticator or any authenticator app.<br />
        2 · Scan this code (or type the key).<br />
        3 · Type the 6-digit code the app shows.
      </p>
      {loadErr && <InlineError text={loadErr} />}
      {!start && !loadErr && <div className="sec-loading">Creating your set-up code…</div>}
      {start && (
        <>
          <div className="sc-qr"><QrCode text={start.otpauthUri} label="QR code for your authenticator app" /></div>
          <div className="sc-key" aria-label="Set-up key">{groupKey(start.secret)}</div>
          <a className="sc-uri" href={start.otpauthUri}>On this phone? Open it in your authenticator app</a>
          <div className="mrow" style={{ marginTop: 16 }}>
            <label htmlFor="totpCode">6-digit code from the app</label>
            <input id="totpCode" ref={codeRef} className="sc-code" inputMode="numeric" maxLength={6} autoComplete="one-time-code" placeholder="••••••" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={(e) => { if (e.key === 'Enter') void verify(); }} />
          </div>
        </>
      )}
      {err && <div style={{ marginBottom: 12 }}><InlineError text={err} /></div>}
      <div className="mact">
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        <button type="button" className="ma" disabled={!start || code.length !== 6 || busy} onClick={() => void verify()}>{busy ? 'Checking…' : 'Verify and continue'}</button>
      </div>
    </ScModal>
  );
}

// ── Text message code ──────────────────────────────────────────────────────────
export function TextCodeSetup({ mobile, who, onClose }: { mobile: string | null; who: string; onClose: () => void }) {
  const [step, setStep] = useState<'mobile' | 'code'>('mobile');
  const [num, setNum] = useState(mobile ?? '');
  const [masked, setMasked] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [wait, setWait] = useState(0);
  const [codes, setCodes] = useState<string[] | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);
  useEffect(() => { if (step === 'code') codeRef.current?.focus({ preventScroll: true }); }, [step]);

  const validMobile = digits(num).length === 10 || (digits(num).length === 11 && digits(num).startsWith('1'));
  async function send() {
    if (!validMobile || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await withConfirm(() => accountAuthApi.smsStart(num.trim()), 'You’re adding a text-message code to your account.');
      setMasked(r?.maskedMobile ?? '');
      setWait(r?.resendInSeconds ?? 30);
      setStep('code');
    } catch (e) {
      if (!isConfirmCancelled(e)) setErr(codeErr(e));
    } finally { setBusy(false); }
  }
  async function verify() {
    if (code.length !== 6 || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await withConfirm(() => accountAuthApi.smsConfirm(code));
      notifySecurityChanged();
      if (r?.backupCodes?.length) setCodes(r.backupCodes);
      else { toast('success', 'Text message code set up'); onClose(); }
    } catch (e) {
      if (!isConfirmCancelled(e)) { setErr(codeErr(e)); setCode(''); }
    } finally { setBusy(false); }
  }

  if (codes) return <BackupCodesModal codes={codes} who={who} onClose={() => { toast('success', 'Text message code set up'); onClose(); }} />;

  return (
    <ScModal onClose={onClose} label="Set up a text message code" closeOnScrim={false}>
      <span className="sev info"><Ic k="sms" /></span>
      <h4>Set up a text message code</h4>
      {step === 'mobile' ? (
        <>
          <p>We text a 6-digit code to this number each time you sign in.</p>
          <div className="mrow"><label htmlFor="smsMobile">Mobile number</label>
            <input id="smsMobile" type="tel" inputMode="tel" autoComplete="tel" value={num}
              onChange={(e) => setNum(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void send(); }} /></div>
        </>
      ) : (
        <>
          <p>We sent a 6-digit code to {masked || 'your mobile'}. It works for 10 minutes.</p>
          <div className="mrow"><label htmlFor="smsCode">Code</label>
            <input id="smsCode" ref={codeRef} className="sc-code" inputMode="numeric" maxLength={6} autoComplete="one-time-code" placeholder="••••••" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={(e) => { if (e.key === 'Enter') void verify(); }} /></div>
          <div className="sc-btnrow" style={{ marginTop: -4, marginBottom: 14 }}>
            <button type="button" className="sc-link" disabled={wait > 0 || busy} onClick={() => void send()}>{wait > 0 ? `Resend in ${wait}s` : 'Send the code again'}</button>
            <button type="button" className="sc-link" onClick={() => { setStep('mobile'); setCode(''); setErr(''); }}>Use a different number</button>
          </div>
        </>
      )}
      {err && <div style={{ marginBottom: 12 }}><InlineError text={err} /></div>}
      <div className="mact">
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        {step === 'mobile'
          ? <button type="button" className="ma" disabled={!validMobile || busy} onClick={() => void send()}><Ic k="send" /> {busy ? 'Sending…' : 'Text me a code'}</button>
          : <button type="button" className="ma" disabled={code.length !== 6 || busy} onClick={() => void verify()}>{busy ? 'Checking…' : 'Verify and continue'}</button>}
      </div>
    </ScModal>
  );
}
